import { Loader2 } from 'lucide-react'
import { type ChangeEvent, useEffect, useId, useRef, useState } from 'react'

import useEnterpriseBusinessImportCancel from 'src/features/enterprise-upload-leads/api/use-enterprise-business-import-cancel'
import useEnterpriseBusinessImportPreview from 'src/features/enterprise-upload-leads/api/use-enterprise-business-import-preview'
import { FileUploadIcon } from 'src/shared/features/enterprise/icons/file-upload-icon'
import { useMyUserData } from 'src/shared/features/users/api/accounts/use-my-user'
import { useToast } from 'src/shared/hooks/use-toast'
import { cn } from 'src/shared/utils/cn'

import type { UploadedFileMeta } from '../store/upload-leads-store'

import UploadedFileCard from './uploaded-file-card'

interface LocalUploadedFileMeta {
  file: File
  name: string
  size: number
  type: string
  uploadId?: string
}

export type LocalUploadStatus = 'idle' | 'uploading' | 'uploaded'

const DEFAULT_MAX_FILE_SIZE_BYTES = 5 * 1024 * 1024
const DEFAULT_UPLOAD_INTERVAL_MS = 100
const DEFAULT_UPLOAD_STEP = 4

const isCsvFile = (file: File) => {
  const mimeType = file.type.toLowerCase()
  const fileName = file.name.toLowerCase()
  return mimeType === 'text/csv' || mimeType === 'application/vnd.ms-excel' || fileName.endsWith('.csv')
}

const getLocalMetaFromFile = (file: File): LocalUploadedFileMeta => {
  return {
    file,
    name: file.name,
    size: file.size,
    type: file.type || 'text/csv',
  }
}

const getLocalMetaFromUploadedFile = (uploadedFile: UploadedFileMeta): LocalUploadedFileMeta => {
  return {
    file: uploadedFile.file,
    name: uploadedFile.name,
    size: uploadedFile.size,
    type: uploadedFile.type,
    uploadId: uploadedFile.previewData.uploadId,
  }
}

interface FileUploadBoxProps {
  initialFile?: UploadedFileMeta | null
  onFileChange?: (fileMeta: UploadedFileMeta | null) => void
  accept?: string
  maxFileSizeBytes?: number
  uploadIntervalMs?: number
  uploadStep?: number
  className?: string
  /** Increment this value to remove current uploaded file. */
  requestRemoveFileToken?: number
}

export default function FileUploadBox({
  initialFile = null,
  onFileChange,
  accept = '.csv,text/csv',
  maxFileSizeBytes = DEFAULT_MAX_FILE_SIZE_BYTES,
  uploadIntervalMs = DEFAULT_UPLOAD_INTERVAL_MS,
  uploadStep = DEFAULT_UPLOAD_STEP,
  className,
  requestRemoveFileToken = 1,
}: FileUploadBoxProps) {
  const { data: userData } = useMyUserData()
  const { mutateAsync: previewBusinessImport } = useEnterpriseBusinessImportPreview()
  const { mutate: cancelBusinessImport } = useEnterpriseBusinessImportCancel()
  const { toast } = useToast()
  const inputId = useId()
  const fileInputRef = useRef<HTMLInputElement | null>(null)
  const [uploadStatus, setUploadStatus] = useState<LocalUploadStatus>(initialFile ? 'uploaded' : 'idle')
  const [uploadProgress, setUploadProgress] = useState(initialFile ? 100 : 0)
  const [pendingFileMeta, setPendingFileMeta] = useState<LocalUploadedFileMeta | null>(
    initialFile ? getLocalMetaFromUploadedFile(initialFile) : null,
  )

  const requestRemoveFileTokenRef = useRef(requestRemoveFileToken)

  useEffect(() => {
    if (requestRemoveFileToken !== requestRemoveFileTokenRef.current) {
      requestRemoveFileTokenRef.current = requestRemoveFileToken
      handleRemoveFile()
    }
  }, [requestRemoveFileToken])

  const resetLocalUploadUi = () => {
    setUploadStatus('idle')
    setUploadProgress(0)
    setPendingFileMeta(null)
  }

  useEffect(() => {
    if (!initialFile) {
      resetLocalUploadUi()
      return
    }

    setUploadStatus('uploaded')
    setUploadProgress(100)
    setPendingFileMeta(getLocalMetaFromUploadedFile(initialFile))
  }, [initialFile])

  useEffect(() => {
    if (uploadStatus !== 'uploading' || !pendingFileMeta) return

    const completeUpload = async () => {
      const enterpriseId = userData?.enterpriseId
      if (!enterpriseId) {
        toast({
          title: 'Could not upload file',
          description: 'Missing enterprise context. Please refresh and try again.',
          variant: 'destructive',
        })
        resetLocalUploadUi()
        onFileChange?.(null)
        if (fileInputRef.current) {
          fileInputRef.current.value = ''
        }
        return
      }

      try {
        const previewData = await previewBusinessImport({
          enterpriseId,
          file: pendingFileMeta.file,
        })
        const uploadedFileMeta = {
          ...pendingFileMeta,
          previewData,
        }
        setPendingFileMeta((currentMeta) =>
          currentMeta
            ? {
                ...currentMeta,
                uploadId: previewData.uploadId,
              }
            : currentMeta,
        )
        setUploadStatus('uploaded')
        onFileChange?.(uploadedFileMeta)
      } catch (error) {
        console.error(error)
        toast({
          title: 'Could not process file',
          description: 'The import preview failed. Please try again with a valid CSV.',
          variant: 'destructive',
        })
        resetLocalUploadUi()
        onFileChange?.(null)
        if (fileInputRef.current) {
          fileInputRef.current.value = ''
        }
      }
    }

    const intervalId = window.setInterval(() => {
      setUploadProgress((currentProgress) => {
        const nextProgress = Math.min(100, currentProgress + uploadStep)
        if (nextProgress >= 100) {
          window.clearInterval(intervalId)
          void completeUpload()
        }
        return nextProgress
      })
    }, uploadIntervalMs)

    return () => {
      window.clearInterval(intervalId)
    }
  }, [
    onFileChange,
    pendingFileMeta,
    previewBusinessImport,
    toast,
    uploadIntervalMs,
    uploadStatus,
    uploadStep,
    userData?.enterpriseId,
  ])

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return

    if (!isCsvFile(file) || file.size > maxFileSizeBytes) {
      resetLocalUploadUi()
      event.target.value = ''
      onFileChange?.(null)
      return
    }

    setPendingFileMeta(getLocalMetaFromFile(file))
    setUploadProgress(0)
    setUploadStatus('uploading')
  }

  const handleRemoveFile = () => {
    const enterpriseId = userData?.enterpriseId
    const uploadId = pendingFileMeta?.uploadId

    resetLocalUploadUi()
    onFileChange?.(null)
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }

    if (enterpriseId && uploadId) {
      cancelBusinessImport({ enterpriseId, uploadId })
    }
  }

  const isUploading = uploadStatus === 'uploading'
  const isUploaded = uploadStatus === 'uploaded'

  return isUploaded ? (
    <UploadedFileCard
      fileName={pendingFileMeta?.name ?? 'Uploaded leads file'}
      fileSize={pendingFileMeta?.size ?? 0}
      onRemove={handleRemoveFile}
      className={className}
    />
  ) : (
    <label
      htmlFor={inputId}
      className={cn(
        'group relative flex h-[220px] w-full cursor-pointer flex-col items-center justify-center rounded-[20px] bg-white px-[110px] py-[43px] text-center',
        isUploading && 'cursor-default',
        !isUploading && 'hover:bg-accent',
        className,
      )}
    >
      {/* Dashed border */}
      <svg className="pointer-events-none absolute inset-0 size-full" aria-hidden="true">
        <rect
          x="0.5"
          y="0.5"
          width="calc(100% - 1px)"
          height="calc(100% - 1px)"
          rx="20"
          ry="20"
          fill="none"
          stroke="currentColor"
          strokeWidth="1"
          strokeDasharray="7 7"
          className="text-border"
        />
      </svg>

      <input
        ref={fileInputRef}
        id={inputId}
        type="file"
        accept={accept}
        className="hidden"
        aria-label="Upload leads CSV file"
        onChange={handleFileChange}
      />

      <div
        className={cn(
          'mb-5 flex size-[60px] items-center justify-center rounded-full bg-accent',
          !isUploading && 'group-hover:bg-white',
        )}
      >
        {isUploading ? (
          <Loader2 className="size-[22px] animate-spin text-primary" />
        ) : (
          <FileUploadIcon size={22} className="text-primary" />
        )}
      </div>

      <p className="text-[16px] font-semibold leading-[1.35] tracking-[-0.16px] text-primary">
        {isUploading ? 'Uploading file' : 'Tap to upload file'}
      </p>
      <p className="mt-2.5 text-[13px] font-medium leading-none text-muted">
        {isUploading ? `${uploadProgress}%` : 'CSV file (max 5MB)'}
      </p>
    </label>
  )
}
